/**
 * Broadcast HUD (design 05 · UI — In-Game HUD Mock SZ3vH + HUD Kit JZWvQ). Reads ONLY `ui.hud` (HudSnapshot),
 * `ui.matchSetup`, the one-shot `ui.pulse`, `ui.lowerThird`, `ui.captions`, `ui.tutorial` and `ui.practice`.
 *
 *   top-left / top-right   stamina bar (MUST BE REAL) + power meter with the special icon (FULL glow)
 *   top-center            play clock pill · rush count ring ("2 MISSISSIPPI…" → GO!)
 *   bottom-left           down & distance chyron (1ST DOWN flash) · lower-thirds
 *   bottom-right          HFN scorebug
 *   center                conversion choice prompt · pre-snap control hints · announcer captions
 */
import './hud.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { MatchFormat, RULES, YARDS_FOR_FIRST_DOWN } from '@hf/shared';
import { Phase, PlayCall, type HudSnapshot } from '@hf/sim';
import { useActions, useServices } from '../actions';
import {
  ballOn,
  downAndDistance,
  isKickoffCall,
  kickHeadline,
  playCallTag,
  playClock,
  tryPlayLabel,
} from '@hf/client-core/ui-model/format';
import { ui } from '@hf/client-core/ui-model/store';
import type { InputManager } from '@hf/client-core/input/types';
import type { MatchSetup } from '@hf/client-core/ui-model/types';
import { ButtonCap, promptDevice, usePulse } from './common';
import { MatchIntro } from './MatchIntro';
import { Meters } from './Meters';
import { PracticePanel } from './PracticePanel';
import { ReplayBanner } from './ReplayBanner';
import { Scorebug } from './Scorebug';
import { TutorialCard } from './TutorialCard';
import {
  type HintTrack,
  newHintTrack,
  trackLineup,
  noteHumanOffside,
  showDefenseHint,
  showOffenseHint,
} from '@hf/client-core/ui-model/hud';
export {
  HINT_BUDGET,
  type HintTrack,
  newHintTrack,
  trackLineup,
  noteHumanOffside,
  showDefenseHint,
  showOffenseHint,
} from '@hf/client-core/ui-model/hud';

const coarsePointer = (): boolean => {
  try {
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
};

/**
 * Should the HUD use the touch layout (graphics stacked top-centre, clear of the on-screen pad)? Keyed off the
 * touch overlay's REAL visibility, so touchControls 'on' on a desktop and 'off' on a phone both lay out correctly.
 * While an overlay is open the touch pad is suspended (hidden), so the previous answer is kept to avoid the HUD
 * jumping under the pause menu. Falls back to `(pointer: coarse)` when the InputManager has no touch status.
 */
export const touchLayoutOn = (
  input: Pick<InputManager, 'touch'> | null | undefined,
  overlayOpen: boolean,
  prev: boolean,
): boolean => {
  const t = (input as { touch?: InputManager['touch'] } | null | undefined)?.touch;
  if (!t || typeof t.visible !== 'boolean') return coarsePointer();
  if (t.visible) return true;
  if (overlayOpen && t.available) return prev;
  return false;
};

/** Live `touchLayoutOn`: re-evaluated when the InputManager announces a touch status change (no polling). */
const useTouchLayout = (): boolean => {
  const { input } = useServices();
  const overlayOpen = ui.overlay.value !== 'none';
  const [, bump] = useState(0);
  const prev = useRef(false);
  useEffect(() => {
    if (typeof input.onTouchChanged !== 'function') return;
    return input.onTouchChanged(() => bump((n) => n + 1));
  }, [input]);
  const next = touchLayoutOn(input, overlayOpen, prev.current);
  prev.current = next;
  return next;
};

export function Hud() {
  const h = ui.hud.value;
  const setup = ui.matchSetup.value;
  const touchOn = useTouchLayout();
  const intro = ui.matchIntro.value;
  // "TAKING THE FIELD" covers the screen until the first tick ran: no empty HUD over a blank stadium.
  const curtain = intro !== null && intro.phase !== 'ready';
  const replaying = ui.replay.value !== null;
  if (!h || curtain)
    return (
      <div class={`hf-hud curtain ${touchOn ? 'touch-on' : ''}`} data-testid="hud">
        <MatchIntro />
      </div>
    );
  const specialsOn = h.special[0].id !== 0 || h.special[1].id !== 0;
  return (
    <div
      class={`hf-hud phase-${h.phase} ${touchOn ? 'touch-on' : ''} ${replaying ? 'replaying' : ''}`}
      data-testid="hud"
    >
      <Meters h={h} side={0} setup={setup} specials={specialsOn} />
      <Meters h={h} side={1} setup={setup} specials={specialsOn} />
      <TopCenter h={h} />
      <Chyron h={h} />
      <LowerThirdBand />
      <Scorebug h={h} setup={setup} />
      <ConversionPrompt h={h} setup={setup} />
      <SnapHint h={h} setup={setup} />
      <KneelHint h={h} setup={setup} />
      <Captions />
      <TutorialCard setup={setup} />
      <PracticePanel />
      <ReplayBanner />
      <MatchIntro />
    </div>
  );
}

/** Play clock pill + rush count ring + GO! burst + a pause button for mouse users. */
function TopCenter({ h }: { h: HudSnapshot }) {
  const actions = useActions();
  const go = usePulse('go', 900);
  // Football 101's lesson-clear / YOU MADE THE TEAM cards end the play: the clock behind them stops counting (QA r3)
  const tutPhase = ui.tutorial.value?.phase;
  const carded = tutPhase === 'complete' || tutPhase === 'clear';
  const preSnap = h.phase === Phase.PRE_SNAP && !carded;
  const rush = h.phase === Phase.RUSH_COUNT && !carded;
  const total = Math.max(1, Math.round(RULES.rushCountFrames / 60));
  const count = Math.min(total, Math.max(1, total - h.rushCountSec + 1));
  const warn = preSnap && h.playClockSec <= RULES.playClockWarningSec;
  return (
    <div class="hf-hud__top">
      <div class="hf-hud__clockrow">
        <button class="hf-hud__pause interactive" aria-label="Pause" onClick={() => actions.pause()}>
          <span />
          <span />
        </button>
        <div class={`hf-playclock ${preSnap ? '' : 'idle'} ${warn ? 'warn' : ''}`} aria-label="Play clock">
          <span class="hf-playclock__label t-broadcast">PLAY</span>
          <span class="hf-playclock__val t-broadcast" key={warn ? h.playClockSec : 'v'}>
            {preSnap ? playClock(h.playClockSec) : !carded && (h.phase === Phase.LIVE || rush) ? 'LIVE' : '—'}
          </span>
        </div>
      </div>
      {rush ? (
        <div class="hf-rush" role="timer" aria-label={`Rush count ${count}`}>
          <div class="hf-rush__ring" style={{ '--p': count / total }}>
            <span class="hf-rush__num t-display" key={count}>
              {count}
            </span>
          </div>
          <div class="hf-rush__pill t-display">{count} MISSISSIPPI…</div>
        </div>
      ) : go && h.phase !== Phase.PRE_SNAP ? (
        /* never over a line-up: GO! means "cross now", and the defense must still wait for the next count */
        <div class="hf-rush go" key={go.id} aria-live="assertive">
          <div class="hf-rush__burst" aria-hidden="true" />
          <div class="hf-rush__ring go">
            <span class="hf-rush__num t-display">GO!</span>
          </div>
          <div class="hf-rush__pill go t-display">GO! GO! GO!</div>
        </div>
      ) : null}
    </div>
  );
}

/** Down & distance chyron with the 1ST DOWN flash (HUD Kit B). */
function Chyron({ h }: { h: HudSnapshot }) {
  const fd = usePulse('first_down', 1800);
  const tag = playCallTag(h.playCall);
  if (h.phase === Phase.GAME_OVER) return null;
  if (fd)
    return (
      <div class="hf-chyron first" key={fd.id} aria-live="polite">
        <div class="hf-chyron__main t-display">1ST DOWN!</div>
        <div class="hf-chyron__chip t-broadcast">{fd.text ?? `& ${YARDS_FOR_FIRST_DOWN}`}</div>
      </div>
    );
  const conversion = h.phase === Phase.CONVERSION;
  // A conversion try is ONE play: never a down & distance ("1ST & GOAL" misled newcomers on the 2-point play).
  const tryLabel = conversion ? null : tryPlayLabel(h.tryPlay);
  // Kick plays headline the kick; a punt keeps its down & distance as the tag ("PUNT · 4TH & 7").
  const kick = conversion || tryLabel ? null : kickHeadline(h.playCall);
  const chip =
    conversion || tryLabel
      ? tryLabel && h.tryPlay === 'two_point'
        ? 'ONE PLAY'
        : null
      : kick
        ? isKickoffCall(h.playCall)
          ? null
          : downAndDistance(h)
        : tag;
  return (
    <div class={`hf-chyron ${kick ? 'kick' : ''} ${tryLabel ? 'try' : ''}`} aria-label="Down and distance">
      <div class="hf-chyron__main t-broadcast">
        {conversion ? 'CONVERSION' : (tryLabel ?? kick ?? downAndDistance(h))}
        {chip ? <span class="hf-chyron__tag">{chip}</span> : null}
      </div>
      <div class="hf-chyron__ball">
        <small class="t-broadcast">BALL ON</small>
        <b class="t-broadcast">{ballOn(h.losLabel, h.losOwnSide)}</b>
      </div>
    </div>
  );
}

function LowerThirdBand() {
  const l = ui.lowerThird.value;
  if (!l) return null;
  return (
    <div class={`hf-lower ${l.tone}`} key={l.id} role="status">
      <span class="hf-lower__bar" aria-hidden="true" />
      <span class="hf-lower__title t-display">{l.title}</span>
      {l.sub ? <span class="hf-lower__sub t-broadcast">{l.sub}</span> : null}
    </div>
  );
}

/** TD scored: "GO FOR 2 or KICK IT" choice for the scorer, with the countdown. */
function ConversionPrompt({ h, setup }: { h: HudSnapshot; setup: MatchSetup | null }) {
  const { input } = useServices();
  if (!h.conversionPending) return null;
  const off = (h.offense === 1 ? 1 : 0) as 0 | 1;
  const human = setup?.slots[off].kind === 'human';
  const remote = setup?.slots[off].kind === 'remote';
  const device = promptDevice(setup, off, input.lastActiveDevice());
  const total = Math.round(RULES.conversionChoiceFrames / 60);
  return (
    <div class={`hf-convert ${off === 1 ? 'away' : 'home'}`} role="dialog" aria-label="Conversion choice">
      <div class="hf-convert__title t-display">EXTRA POINT OR GO FOR TWO?</div>
      <div class="hf-convert__opts">
        <div class="hf-convert__opt">
          {human ? <ButtonCap device={device} button="A" /> : null}
          <b class="t-display">1-PT TOSS</b>
          <small>Toss it through the uprights from the 15</small>
        </div>
        <div class="hf-convert__or t-display">OR</div>
        <div class="hf-convert__opt two">
          {human ? <ButtonCap device={device} button="U" /> : null}
          <b class="t-display">2-PT PLAY</b>
          <small>Run or throw it in from the 3</small>
        </div>
      </div>
      <div class="hf-convert__timer t-broadcast" style={{ '--p': h.conversionSec / total }}>
        {human
          ? `${h.conversionSec}s · TOSS ON TIMEOUT`
          : remote
            ? 'OPPONENT IS CHOOSING…'
            : 'CPU IS CHOOSING…'}
      </div>
    </div>
  );
}

/**
 * Pre-snap control reminder for a human offense, and the "WAIT FOR GO" reminder for a human defense (kept up
 * through the rush count, when crossing early is a penalty). Both fade after a few line-ups; the defense one comes
 * back after an OFFSIDE by the human. Shown alongside the drive lower-third (it sits higher, centred).
 */
function SnapHint({ h, setup }: { h: HudSnapshot; setup: MatchSetup | null }) {
  const { input } = useServices();
  const track = useRef<HintTrack | null>(null);
  if (!track.current) track.current = newHintTrack();
  const t = track.current;
  const [, bump] = useState(0);
  const off = (h.offense === 1 ? 1 : 0) as 0 | 1;
  const def = (off === 0 ? 1 : 0) as 0 | 1;
  const kickoff = isKickoffCall(h.playCall);
  trackLineup(t, h.phase, {
    offense: setup?.slots[off].kind === 'human',
    defense: setup?.slots[def].kind === 'human',
    kickoff,
  });
  useEffect(
    () =>
      ui.onSimEvents((events) => {
        for (const e of events) {
          if (e.type !== 'OFFSIDE') continue;
          const s = ui.matchSetup.peek();
          if (s?.slots[e.actor === 1 ? 1 : 0].kind !== 'human') continue;
          noteHumanOffside(t);
          bump((n) => n + 1);
        }
      }),
    [],
  );
  // Football 101's lesson-clear / YOU MADE THE TEAM cards end the play: the clock behind them stops counting (QA r3)
  const tutPhase = ui.tutorial.value?.phase;
  const carded = tutPhase === 'complete' || tutPhase === 'clear';
  const preSnap = h.phase === Phase.PRE_SNAP && !carded;
  const rush = h.phase === Phase.RUSH_COUNT && !carded;
  if ((!preSnap && !rush) || h.conversionPending || ui.tutorial.value) return null;
  const tutorialish = setup?.mode === 'tutorial' || setup?.mode === 'practice';
  const offDev = promptDevice(setup, off, input.lastActiveDevice());
  const defDev = promptDevice(setup, def, input.lastActiveDevice());
  const defHint = !!defDev && (tutorialish || showDefenseHint(t));
  const offside = t.offsideAt >= 0 && t.def === t.offsideAt + 1;
  if (rush) {
    // The count is running: crossing now is OFFSIDE. Only the defender needs telling.
    if (!defHint || kickoff) return null;
    return (
      <div class={`hf-snaphint def wait-go ${def === 1 ? 'away' : 'home'}`} data-testid="def-hint">
        <span class="hf-snaphint__tag t-display">WAIT FOR GO!</span>
        <span>DON'T CROSS THE BLUE LINE YET</span>
      </div>
    );
  }
  // 4th down always gets its reminder (punt / field goal), even once the general hint has faded.
  const fourth = h.down === 4 && !kickoff && !h.tryPlay;
  const isFg = h.playCall === PlayCall.FG_TOSS;
  // The sim ignores a snap press in the first moments after the line-up: the prompt waits for it too.
  const ready = ui.snapReady.value;
  // Punts are Classic regulation only (overtime is college-style: no kicks).
  const punt = (h.format ?? setup?.config.format) === MatchFormat.CLASSIC && !h.overtime;
  if (kickoff) return <KickoffHint off={off} offDev={offDev} defDev={defDev} ready={ready} />;
  if (offDev && (tutorialish || fourth || showOffenseHint(t)))
    return (
      <div
        class={`hf-snaphint ${off === 1 ? 'away' : 'home'} ${ready ? '' : 'wait'} ${fourth ? 'fourth' : ''}`}
      >
        {fourth ? <span class="hf-snaphint__tag t-display">4TH DOWN</span> : null}
        <span class="hf-snaphint__snap">
          {ready ? (
            <>
              <ButtonCap device={offDev} button="A" /> SNAP
            </>
          ) : (
            'GET SET…'
          )}
        </span>
        {h.tryPlay ? null : (
          <span>
            HOLD <ButtonCap device={offDev} button="D" /> + <ButtonCap device={offDev} button="A" />{' '}
            {isFg ? 'FG' : 'FIELD GOAL'}
          </span>
        )}
        {punt && !h.tryPlay ? (
          <span>
            HOLD <ButtonCap device={offDev} button="S" /> + <ButtonCap device={offDev} button="A" /> PUNT
          </span>
        ) : null}
        {fourth ? null : (
          <span>
            <ButtonCap device={offDev} button="U" /> HARD COUNT
          </span>
        )}
      </div>
    );
  if (offDev) return null;
  if (defHint)
    return (
      <div
        class={`hf-snaphint def ${def === 1 ? 'away' : 'home'} ${offside ? 'offside' : ''}`}
        data-testid="def-hint"
      >
        <span class="hf-snaphint__tag t-display">{offside ? 'OFFSIDE!' : 'DEFENSE'}</span>
        <span>STAY BEHIND THE LINE UNTIL GO!</span>
        <span>
          <ButtonCap device={defDev} button="A" /> DIVE · IN THE AIR: LEAP
        </span>
      </div>
    );
  return null;
}

/** Classic kickoff line-up: the kicker's toss controls, or the receiver's job. */
function KickoffHint({
  off,
  offDev,
  defDev,
  ready,
}: {
  off: 0 | 1;
  offDev: string | null;
  defDev: string | null;
  ready: boolean;
}) {
  if (offDev)
    return (
      <div class={`hf-snaphint kick ${off === 1 ? 'away' : 'home'} ${ready ? '' : 'wait'}`}>
        <span class="hf-snaphint__snap">
          {ready ? (
            <>
              HOLD <ButtonCap device={offDev} button="A" /> POWER · RELEASE TO KICK
            </>
          ) : (
            'GET SET…'
          )}
        </span>
        <span>
          HOLD <ButtonCap device={offDev} button="D" /> AT THE RELEASE: ONSIDE KICK
        </span>
      </div>
    );
  if (defDev)
    return (
      <div class={`hf-snaphint kick ${off === 1 ? 'home' : 'away'}`}>
        <span>KICKOFF: CATCH IT OR SCOOP IT UP</span>
        <span>STAY BACK UNTIL IT'S KICKED</span>
      </div>
    );
  return null;
}

/**
 * Classic kick return: the returner holds the ball in his own end zone — ▼ held takes a knee (touchback). Shown to
 * that player only, in live play, and kept in the touch layout (it is the one live-play prompt).
 */
function KneelHint({ h, setup }: { h: HudSnapshot; setup: MatchSetup | null }) {
  const { input } = useServices();
  const k = h.kneel ?? -1;
  if (h.phase !== Phase.LIVE || (k !== 0 && k !== 1)) return null;
  const dev = promptDevice(setup, k, input.lastActiveDevice());
  if (!dev) return null;
  return (
    <div class={`hf-snaphint kneel ${k === 1 ? 'away' : 'home'}`} role="status" data-testid="kneel-hint">
      <span>
        HOLD <ButtonCap device={dev} button="D" /> TAKE A KNEE
      </span>
      <span class="hf-snaphint__sub">TOUCHBACK</span>
    </div>
  );
}

function Captions() {
  const c = ui.captions.value;
  if (c.length === 0) return null;
  return (
    <div class="hf-captions" aria-live="polite">
      {c.map((x) => (
        <div class="hf-caption" key={x.id}>
          <span class="hf-caption__mic" aria-hidden="true">
            ◉
          </span>
          {x.text}
        </div>
      ))}
    </div>
  );
}
